import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import QuestionService from '../services/QuestionService';
import QuestionAnswerService from '../services/QuestionAnswerService';
import { Row, Image, Col, Button, Card, Modal, Space } from 'antd';
import SubjectService from '../services/SubjectService';
import { openNotification } from '../utils';

interface FilesUrl {
    [key: string]: string;  // Her anahtarın string ve her değerin string olduğu bir obje tipi tanımlıyoruz.
}

export const createQuestionImageUrl = (fileResponse: any): string => {
    const contentType = fileResponse?.headers?.['content-type']
        || fileResponse?.headers?.get?.('content-type')
        || 'image/jpeg';
    const imageBlob = new Blob([fileResponse?.data], { type: contentType });

    return URL.createObjectURL(imageBlob);
};

const Questions: React.FC = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [questions, setQuestions] = useState<any[]>([]);
    const [selectedValue, setSelectedValue] = useState<Record<string, string>>({});
    const [filesUrl, setFilesUrl] = useState<Record<string, string>>({});
    const [title, setTitle] = useState<any>({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);  // Aktif soru indeksi
    const [isFinished, setIsFinished] = useState<boolean>(false);
    const [result, setResult] = useState<{ correct: number; wrong: number }>({ correct: 0, wrong: 0 });
    const [saving, setSaving] = useState<boolean>(false);
    const [resetting, setResetting] = useState<boolean>(false);
    const navigate = useNavigate()

    const handleSelect = (questionId: number | string, value: string) => {
        const key = String(questionId);
        setSelectedValue(prev => {
            if (prev[key] === value) {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            }
            return { ...prev, [key]: value };
        });
    };

    const renderOptions = (question: any) => {
        const options = ['A', 'B', 'C', 'D', 'E'].slice(0, question?.answerType);
        const questionKey = String(question?.id);
        return (
            <Row gutter={[16, 16]} className='mt-24'>
                {options.map(option => (
                    <Col key={option}>
                        <Card
                            hoverable
                            onClick={() => handleSelect(question.id, option)}
                            style={{
                                backgroundColor: selectedValue[questionKey] === option ? '#722ed1' : '#f0f0f0',
                                color: selectedValue[questionKey] === option ? '#fff' : '#000',
                                textAlign: 'center',
                                fontSize: 18,
                                width: 80
                            }}
                        >
                            {option}
                        </Card>
                    </Col>
                ))}
            </Row>
        );
    };

    const loadDetails = async (qs: any[]) => {
        const urls: FilesUrl = {};
        for (let question of qs) {
            try {
                const fileResponse = await QuestionService.getFile(question.question);
                urls[question.id] = createQuestionImageUrl(fileResponse);
            } catch (error) {
                console.error('Error downloading file:', error);
                urls[question.id] = '';
            }
        }
        setFilesUrl(urls);
    };

    const loadSavedAnswers = async (subjectId: string | undefined, qs: any[]) => {
        if (!subjectId) {
            return;
        }
        try {
            const response = await QuestionAnswerService.getSubjectAnswers(subjectId);
            const answers = Array.isArray(response?.data) ? response?.data : [];
            if (!answers.length) {
                setSelectedValue({});
                return;
            }
            const validQuestionIds = new Set(qs.map((question: any) => String(question.id)));
            const mapped: Record<string, string> = {};
            answers.forEach((answer: any) => {
                const key = String(answer.questionId);
                if (validQuestionIds.has(key) && answer.option) {
                    mapped[key] = answer.option;
                }
            });
            setSelectedValue(mapped);
        } catch (error) {
            console.error('Error loading saved answers:', error);
        }
    };

    useEffect(() => {
        getService();
    }, [id]);

    const getService = async () => {
        setLoading(true);
        setIsFinished(false);
        setResult({ correct: 0, wrong: 0 });
        setCurrentQuestionIndex(0);
        setSelectedValue({});
        try {
            const response = await SubjectService.get(id);
            const data: any = Array.isArray(response?.data?.questions)
                ? response?.data.questions.filter((q: any) => q.questionType)
                : [];
            setQuestions(data);
            setTitle(response?.data?.title);
            await Promise.all([
                loadDetails(data),
                loadSavedAnswers(id, data)
            ]);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const goToNext = () => {
        setCurrentQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1));
    };

    const goToPrev = () => {
        setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
    };

    const finishExam = () => {
        let correct = 0;
        let wrong = 0;
        questions.forEach((q) => {
            if (selectedValue[String(q.id)] === q.correctAnswer) {
                correct++;
            } else {
                wrong++;
            }
        });
        setResult({ correct, wrong });
        setIsFinished(true);
    };

    const handleSaveAnswers = async () => {
        if (!id) {
            return;
        }
        setSaving(true);
        try {
            const payload = Object.entries(selectedValue)
                .filter(([, option]) => !!option)
                .map(([questionId, option]) => ({
                    questionId: Number(questionId),
                    option
                }));
            const response = await QuestionAnswerService.saveAllBySubject(id, payload);
            const saved = Array.isArray(response?.data) ? response.data : [];
            const mapped: Record<string, string> = {};
            saved.forEach((answer: any) => {
                if (answer?.questionId && answer?.option) {
                    mapped[String(answer.questionId)] = answer.option;
                }
            });
            if (payload.length === 0) {
                setSelectedValue({});
            } else {
                setSelectedValue(mapped);
            }
            openNotification('success', 'Başarılı', 'Cevaplar kaydedildi.');
        } catch (error) {
            console.error('Error saving answers:', error);
            openNotification('error', 'Başarısız', 'Cevaplar kaydedilemedi.');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (!id) {
            return;
        }
        Modal.confirm({
            title: 'Cevapları Sıfırla',
            content: 'Bu sınava ait tüm cevaplar silinecek. Emin misiniz?',
            okText: 'Evet',
            cancelText: 'Vazgeç',
            okButtonProps: { danger: true },
            async onOk() {
                try {
                    setResetting(true);
                    await QuestionAnswerService.resetSubjectAnswers(id);
                    setSelectedValue({});
                    setIsFinished(false);
                    setResult({ correct: 0, wrong: 0 });
                    setCurrentQuestionIndex(0);
                    openNotification('success', 'Başarılı', 'Cevaplar sıfırlandı.');
                } catch (error) {
                    console.error('Error resetting answers:', error);
                    openNotification('error', 'Başarısız', 'Cevaplar sıfırlanamadı.');
                    throw error;
                } finally {
                    setResetting(false);
                }
            },
        });
    };


    return (
        <>
            {(!isFinished && questions.length > 0) && <Row className='mt-64' gutter={[24, 100]}>
                <Col span={24}>
                    <Card loading={loading} title={title}>
                        <React.Fragment key={questions[currentQuestionIndex]?.id}>
                            <Row justify="center">
                                <Col>
                                    {filesUrl[questions[currentQuestionIndex]?.id] && (
                                        <Image alt="Soru görseli" src={filesUrl[questions[currentQuestionIndex]?.id]} fallback="error.png" />
                                    )}
                                </Col>
                            </Row>
                            <Row className="mt-64" justify="center">
                                <Col>{renderOptions(questions[currentQuestionIndex])}</Col>
                            </Row>
                            <Row align={'middle'} gutter={[24, 24]} justify="space-between">
                                <Col>
                                    <Space>
                                        <Button onClick={goToPrev} disabled={currentQuestionIndex === 0} className="btn fw-700 font-color-white bg-saha-purple">
                                            Geri
                                        </Button>
                                        <Button onClick={goToNext} disabled={currentQuestionIndex === questions.length - 1} className="btn fw-700 font-color-white bg-saha-purple">
                                            İleri
                                        </Button>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        <Button onClick={handleSaveAnswers} loading={saving} type="primary" className="btn fw-700 font-color-white bg-saha-purple">
                                            Kaydet
                                        </Button>
                                        <Button onClick={handleReset} loading={resetting} danger type="primary">
                                            Sıfırla
                                        </Button>
                                        <Button onClick={finishExam} type="primary" className="btn fw-700 font-color-white bg-saha-purple">
                                            Sınavı Bitir
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </React.Fragment>
                    </Card>
                </Col>
            </Row>}
            {(isFinished) && <Row className='mt-64' gutter={[24, 24]}>
                <Col span={8}>
                    <Card
                        title={"Toplam"}
                        style={{
                            color: '#000',
                            textAlign: 'center'
                        }}
                    >
                        <h1 className='m-0 p-0'>{questions?.length}</h1>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card
                        title={"Doğru"}
                        style={{
                            backgroundColor: '#52c41a',
                            color: '#fff',
                            textAlign: 'center'
                        }}
                    >
                        <h1 className='m-0 p-0'>{result.correct}</h1>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card
                        title={"Yanlış"}
                        style={{
                            backgroundColor: '#ff4d4f',

                            color: '#fff',
                            textAlign: 'center'
                        }}
                    >
                        <h1 className='m-0 p-0'>{result.wrong}</h1>
                    </Card>
                </Col>

            </Row>}
        </>
    );
}

export default Questions
